import type { PointerEvent as ReactPointerEvent } from "react";
import type { Tile } from "@word-duel/shared";
import { shuffleTiles, tileDisplayLetter, tileDisplayName } from "../lib/game";
import { Button } from "./ui";

export function Rack({
  tiles,
  placedTileIds,
  selectedTileId,
  draggingTileId,
  disabled,
  onTilesChange,
  onSelect,
  onTilePointerDown
}: {
  tiles: Tile[];
  placedTileIds: Set<string>;
  selectedTileId: string | null;
  draggingTileId: string | null;
  disabled: boolean;
  onTilesChange: (tiles: Tile[]) => void;
  onSelect: (tileId: string) => void;
  onTilePointerDown: (event: ReactPointerEvent<HTMLButtonElement>, tile: Tile) => void;
}) {
  const selectedIndex = tiles.findIndex((tile) => tile.id === selectedTileId);
  const move = (amount: -1 | 1) => {
    if (selectedIndex < 0) return;
    const nextIndex = selectedIndex + amount;
    if (nextIndex < 0 || nextIndex >= tiles.length) return;
    const next = [...tiles];
    const current = next[selectedIndex];
    const other = next[nextIndex];
    if (!current || !other) return;
    next[selectedIndex] = other;
    next[nextIndex] = current;
    onTilesChange(next);
  };

  return (
    <section
      className="rack-section"
      aria-labelledby="rack-title"
      data-testid="rack"
      data-rack-dropzone="true"
    >
      <div className="rack-heading">
        <div>
          <p className="eyebrow">YOUR TILES / {tiles.length}</p>
          <h2 id="rack-title" className="sr-only">
            Your tile rack
          </h2>
        </div>
        <div className="rack-tools">
          <Button
            variant="quiet"
            type="button"
            onClick={() => move(-1)}
            disabled={selectedIndex <= 0}
            aria-label="Move selected tile left"
          >
            ←
          </Button>
          <Button
            variant="quiet"
            type="button"
            onClick={() => move(1)}
            disabled={selectedIndex < 0 || selectedIndex >= tiles.length - 1}
            aria-label="Move selected tile right"
          >
            →
          </Button>
          <Button
            variant="quiet"
            type="button"
            onClick={() => onTilesChange(shuffleTiles(tiles))}
            aria-label="Shuffle rack locally"
            data-testid="rack-shuffle"
          >
            Shuffle
          </Button>
        </div>
      </div>
      <div className="tile-rack" data-rack-dropzone="true">
        {tiles.map((tile, index) => {
          const placed = placedTileIds.has(tile.id);
          const selected = selectedTileId === tile.id;
          const displayLetter = tileDisplayLetter(tile);
          return (
            <button
              key={tile.id}
              type="button"
              className={`rack-tile ${selected ? "is-selected" : ""} ${placed ? "is-placed" : ""} ${draggingTileId === tile.id ? "is-dragging-source" : ""}`}
              onClick={() => onSelect(tile.id)}
              onPointerDown={(event) => onTilePointerDown(event, tile)}
              disabled={disabled || placed}
              aria-pressed={selected}
              aria-label={`${tileDisplayName(tile)} tile, ${tile.value} points, rack position ${index + 1}${placed ? ", placed on board" : ". Drag to the board, or tap to select"}`}
              data-testid="rack-tile"
              data-tile-id={tile.id}
            >
              <b>{displayLetter}</b>
              <small>{tile.value}</small>
            </button>
          );
        })}
        {Array.from({ length: Math.max(0, 7 - tiles.length) }, (_, index) => (
          <span className="rack-slot" aria-hidden="true" key={`slot-${index}`} />
        ))}
      </div>
    </section>
  );
}
